import { useEffect, useRef } from 'react'

/**
 * Lightweight parallax: a single rAF-throttled scroll listener writes one
 * `translate3d` per element. Disabled on small screens and when the user
 * prefers reduced motion, so low-power devices pay nothing for it.
 */
export function useParallax<C extends HTMLElement, T extends HTMLElement>(
  strength = 40,
) {
  const containerRef = useRef<C | null>(null)
  const targetRef = useRef<T | null>(null)

  useEffect(() => {
    const container = containerRef.current
    const target = targetRef.current
    if (!container || !target) return

    const enabled =
      window.matchMedia('(min-width: 1024px)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (!enabled) return

    let frame = 0
    let visible = true

    const update = () => {
      frame = 0
      if (!visible) return
      const rect = container.getBoundingClientRect()
      const progress =
        (window.innerHeight - rect.top) / (window.innerHeight + rect.height)
      const offset = (Math.min(Math.max(progress, 0), 1) - 0.5) * -2 * strength
      target.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`
    }

    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(update)
    }

    const observer =
      typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(
            (entries) => {
              visible = entries[0]?.isIntersecting ?? true
              if (visible) onScroll()
            },
            { rootMargin: '10% 0px' },
          )
        : null

    observer?.observe(container)
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })

    return () => {
      if (frame) cancelAnimationFrame(frame)
      observer?.disconnect()
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [strength])

  return { containerRef, targetRef }
}
